/**
 * Shared color constants for audit metrics
 *
 * Provides consistent colors for Accuracy, Precision, Recall, and F1 Score
 * across all audit interfaces including charts, panels, and reports.
 * Uses XDS design system color variables for proper integration.
 */

import { xdsVars } from '@soteria/design-system-ve';

// Standardized colors for audit metrics using XDS design system variables
export const AUDIT_METRIC_COLORS = {
  accuracy: xdsVars.colors.activityOnline, // Green - for positive/success metrics
  precision: xdsVars.colors.productDataVizWindows, // Blue - actual blue color in XDS
  recall: xdsVars.colors.activityWarning, // Orange - for warning/attention metrics
  f1Score: xdsVars.colors.activityError, // Red - for critical/error metrics
} as const;

// Type for metric color keys
export type AuditMetricType = keyof typeof AUDIT_METRIC_COLORS;

// Helper function to get color by metric type
export function getAuditMetricColor(metric: AuditMetricType): string {
  return AUDIT_METRIC_COLORS[metric];
}

// Helper function to get all metric colors as an array (useful for charts)
export function getAllAuditMetricColors(): string[] {
  return Object.values(AUDIT_METRIC_COLORS);
}

// Mapping for ProgressBar color variants (aligned with XDS design system)
// These variants correspond directly to the XDS colors used above
export const AUDIT_METRIC_PROGRESS_COLORS = {
  accuracy: 'success', // Maps to xdsVars.colors.activityOnline
  precision: 'blue', // Maps to blue variant (matches productDataVizXbox)
  recall: 'warning', // Maps to xdsVars.colors.activityWarning
  f1Score: 'error', // Maps to xdsVars.colors.activityError
} as const;

export type ProgressBarColorVariant = 'primary' | 'success' | 'warning' | 'error' | 'blue';

// Helper function to get ProgressBar color variant by metric type
export function getAuditMetricProgressColor(metric: AuditMetricType): ProgressBarColorVariant {
  return AUDIT_METRIC_PROGRESS_COLORS[metric];
}

// Standardized colors for label certainty metrics using XDS design system variables
export const LABEL_CERTAINTY_COLORS = {
  exactMatches: xdsVars.colors.activityOnline, // Green - for successful/exact matches
  missingLabels: xdsVars.colors.activityWarning, // Orange - for warning/missing items
  additionalLabels: xdsVars.colors.productDataVizWindows, // Blue - for additional/extra items
  bothMissingAndAdditional: xdsVars.colors.productDataVizChevron, // Purple/violet - for mixed/both scenarios
} as const;

// Type for label certainty metric keys
export type LabelCertaintyMetricType = keyof typeof LABEL_CERTAINTY_COLORS;

// Mapping for ProgressBar color variants for label certainty metrics
export const LABEL_CERTAINTY_PROGRESS_COLORS = {
  exactMatches: 'success', // Maps to xdsVars.colors.activityOnline
  missingLabels: 'warning', // Maps to xdsVars.colors.activityWarning
  additionalLabels: 'blue', // Maps to blue variant (productDataVizWindows)
  bothMissingAndAdditional: 'purple', // Maps to purple variant (productDataVizChevron)
} as const;

export type LabelCertaintyProgressBarVariant = 'success' | 'warning' | 'blue' | 'purple';

// Helper function to get color by label certainty metric type
export function getLabelCertaintyColor(metric: LabelCertaintyMetricType): string {
  return LABEL_CERTAINTY_COLORS[metric];
}

// Helper function to get ProgressBar color variant by label certainty metric type
export function getLabelCertaintyProgressColor(metric: LabelCertaintyMetricType): LabelCertaintyProgressBarVariant {
  return LABEL_CERTAINTY_PROGRESS_COLORS[metric];
}
